import { useEffect, useRef, useState } from 'react';

/**
 * A tween's per-frame step. Returning false retires it from the shared loop.
 */
type CountTick = (now: number) => boolean;

/**
 * Every rolling counter on screen shares one rAF loop.
 *
 * Only a counter whose value actually changed rolls, but a resolution volley
 * changes a lot of them at once: a trade plus a location effect can move both
 * stats of several units and both player panels in the same step. One rAF per
 * tween made each of those an independent loop waking the scheduler on its own;
 * one loop drives them all and retires them as they land, so the cost is a single
 * callback per frame no matter how many numbers are moving.
 */
const tickers = new Set<CountTick>();
let frame: number | undefined;

function pump(now: number): void {
  frame = undefined;
  let failure: { error: unknown } | undefined;
  // Iterate a snapshot: a tween that lands is removed during the pass, and one
  // started by a re-render this pass must not run twice on the same timestamp.
  for (const tick of [...tickers]) {
    // A counter that throws retires itself rather than taking every other number
    // on screen down with it — losing the loop would freeze the whole board's
    // stats mid-roll. The pass finishes, then the first error is rethrown so it
    // still surfaces instead of being swallowed.
    try {
      if (!tick(now)) tickers.delete(tick);
    } catch (error) {
      tickers.delete(tick);
      failure ??= { error };
    }
  }
  if (tickers.size > 0) schedule();
  if (failure !== undefined) throw failure.error;
}

function schedule(): void {
  if (frame !== undefined) return;
  // Read the global per call: tests stub rAF after this module is imported.
  frame = globalThis.requestAnimationFrame?.(pump);
}

function subscribeCountTick(tick: CountTick): () => void {
  tickers.add(tick);
  schedule();
  return () => {
    tickers.delete(tick);
    if (tickers.size === 0 && frame !== undefined) {
      globalThis.cancelAnimationFrame?.(frame);
      frame = undefined;
    }
  };
}

/**
 * Smoothly rolls a displayed integer from whatever is currently on screen to
 * `value` over `durationMs` (easeOutCubic) via a shared rAF loop, so stat /
 * resource counters animate instead of snapping to the new number in a single
 * frame.
 *
 * The mount value is shown instantly (no intro roll), an interrupted roll picks
 * up from the number the eye currently sees, and prefers-reduced-motion or an
 * environment without rAF (SSR / node tests) always shows the exact target —
 * so server markup and DOM assertions read the real value, never a mid-roll one.
 */
export function useCountTween(value: number, durationMs = 320): number {
  const [display, setDisplay] = useState(value);
  // The number currently painted, so a change mid-roll continues from there.
  const displayRef = useRef(value);

  useEffect(() => {
    const from = displayRef.current;
    if (from === value) return;
    const animatable = typeof globalThis.requestAnimationFrame === 'function';
    const reduce = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    if (!animatable || reduce || durationMs <= 0 || !Number.isFinite(value)) {
      displayRef.current = value;
      setDisplay(value);
      return;
    }
    const start = globalThis.performance?.now() ?? Date.now();
    return subscribeCountTick((now) => {
      const t = Math.min((now - start) / durationMs, 1);
      const eased = 1 - (1 - t) ** 3;
      const current = t >= 1 ? value : Math.round(from + (value - from) * eased);
      // Only a changed integer is worth a render. A roll of a few points spans
      // ~19 frames at 60Hz but passes through only a handful of distinct
      // numbers, and every setState here re-renders the card that owns the stat.
      if (current !== displayRef.current) {
        displayRef.current = current;
        setDisplay(current);
      }
      return t < 1;
    });
  }, [value, durationMs]);

  return display;
}
